import { useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { X, FileText, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_GROUP_POLICY } from '../../../constants/policies';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

// Map policy names to translation keys (used with checkout namespace)
const POLICY_TYPE_KEYS = {
  group: 'checkout.policyTypeGroup',
  private: 'checkout.policyTypePrivate',
  'w (1:1)': 'checkout.policyTypeWriting',
  writing: 'checkout.policyTypeWriting',
  research: 'checkout.policyTypeResearch',
  'research (1:1)': 'checkout.policyTypeResearch',
};

/**
 * Get translation key for policy type
 */
function getPolicyTypeKey(policyName) {
  const normalized = (policyName || 'group').toLowerCase();
  return POLICY_TYPE_KEYS[normalized] || 'checkout.policyTypeGroup';
}

/**
 * Extract unique policies from selected courses
 * Groups courses by their policy, returns max 4 unique policies
 */
function extractUniquePolicies(courses) {
  const policyMap = new Map();

  courses.forEach(course => {
    // Determine the policy key and content
    const policy = course.policy || DEFAULT_GROUP_POLICY;
    const customText = course.custom_policy_text;
    const customTextKr = course.custom_policy_text_kr;

    // Create a unique key based on policy content
    const policyKey = customText
      ? `custom_${customText.substring(0, 50)}`
      : `policy_${policy.id}`;

    if (!policyMap.has(policyKey)) {
      policyMap.set(policyKey, {
        policy,
        customText,
        customTextKr,
        courses: [],
      });
    }

    policyMap.get(policyKey).courses.push(course.course_title);
  });

  return Array.from(policyMap.values());
}

function CoursePoliciesModalContent({ onClose, onDisagree, courses }) {
  const { t, i18n } = useTranslation('common');
  const contentRef = useRef(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  // Language switcher only controls policy text content, not UI elements
  const [policyLanguage, setPolicyLanguage] = useState(i18n.language === 'ko' ? 'kr' : 'en');

  // Extract unique policies from courses
  const uniquePolicies = useMemo(() => extractUniquePolicies(courses), [courses]);

  // Check if user has scrolled to the bottom
  const handleScroll = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setHasReachedEnd(true);
      }
    }
  }, []);

  // Callback ref to measure content and auto-enable if it fits without scrolling
  const measureRef = useCallback((node) => {
    contentRef.current = node;
    if (node) {
      const { scrollHeight, clientHeight } = node;
      if (scrollHeight <= clientHeight) {
        setHasReachedEnd(true);
      }
    }
  }, []);

  // Check if any policy has Korean text
  const hasAnyKoreanText = useMemo(() => {
    return uniquePolicies.some(({ policy, customTextKr }) => {
      return customTextKr || policy.description_kr;
    });
  }, [uniquePolicies]);

  return (
    <div className="flex items-center justify-center p-4 h-full">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onDisagree}
      />

      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#589DD5]" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('checkout.coursePoliciesTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Warning Banner */}
        <div className="px-4 pt-4 pb-2">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              {t('checkout.coursePoliciesWarning')}
            </p>
          </div>
        </div>

        {/* Policy Count & Language Toggle */}
        <div className="px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('checkout.policiesApplied', { count: uniquePolicies.length })}
          </span>
          {hasAnyKoreanText && (
            <div className="flex items-center gap-1">
              <Languages className="w-3 h-3 text-gray-400 dark:text-gray-500" />
              <div className="flex rounded overflow-hidden border border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => setPolicyLanguage('kr')}
                  className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                    policyLanguage === 'kr'
                      ? 'bg-[#589DD5] text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  KR
                </button>
                <button
                  onClick={() => setPolicyLanguage('en')}
                  className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                    policyLanguage === 'en'
                      ? 'bg-[#589DD5] text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  EN
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div
          ref={measureRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-4 pb-4 overscroll-contain"
          style={{ maxHeight: '50vh' }}
        >
          <div className="space-y-4">
            {uniquePolicies.map(({ policy, customText, customTextKr, courses: courseTitles }, index) => {
              // Determine the text to display based on policy language switcher
              const englishText = customText || policy.description;
              const koreanText = customTextKr || policy.description_kr;
              const displayText = policyLanguage === 'kr' && koreanText ? koreanText : englishText;
              const policyTypeKey = getPolicyTypeKey(policy.display_name || policy.name);

              return (
                <div
                  key={index}
                  className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3"
                >
                  {/* Policy Header */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                      {t('checkout.policyLabel', { name: t(policyTypeKey) })}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {index + 1} / {uniquePolicies.length}
                    </span>
                  </div>

                  {/* Courses this policy applies to */}
                  <div className="mb-3 p-2 bg-white/50 dark:bg-gray-800/50 rounded border border-blue-100 dark:border-blue-800">
                    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      {t('checkout.appliesTo')}
                    </p>
                    <ul className="text-xs text-gray-700 dark:text-gray-300">
                      {courseTitles.map((title, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span>{title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Policy Text - controlled by local language switcher */}
                  {displayText ? (
                    <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {displayText}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                      {t('checkout.noPolicyText', 'No policy text available for this course.')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            {t('checkout.coursePolicyAcknowledgement')}
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={onDisagree}
            className="flex-1 py-2.5 px-4 font-medium rounded-lg transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {t('checkout.iDisagree')}
          </button>
          <button
            onClick={onClose}
            disabled={!hasReachedEnd}
            className={`flex-1 py-2.5 px-4 font-medium rounded-lg transition-colors ${
              hasReachedEnd
                ? 'bg-[#589DD5] hover:bg-[#4a8bc4] text-white'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            {t('checkout.iAgree')}
          </button>
        </div>

        {/* Scroll hint when not at bottom */}
        {!hasReachedEnd && (
          <div className="absolute bottom-20 left-0 right-0 flex justify-center pointer-events-none">
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-600">
              {t('checkout.scrollToAgree')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CoursePoliciesModal({ isOpen, onClose, onDisagree, courses = [], zIndex = 200 }) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex }}>
        <CoursePoliciesModalContent
          onClose={onClose}
          onDisagree={onDisagree}
          courses={courses}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}
